import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DECIMAL_PRECISION_STABLE_COIN,
  TransactionAmountType,
} from '@libs/backend-common';
import { CurrencySelectInput, SelectData } from '@libs/components';
import {
  convertExponentialToDecimal,
  getEstimatedAmount,
  roundDecimal,
} from '@libs/helpers';
import { useDebounce } from '@libs/hooks';

import { DesertedIcon, SwapIconGe } from 'src/assets';
import { ConvertCommission } from 'src/components';
import { CurrencyType, TradeType } from 'src/constants';
import { getAlternativeAssetLabel } from 'src/helpers';
import {
  useBalanceInSelectedCurrency,
  useExchangeOptionsGe,
  useExchangeRateGe,
  useTranslation,
} from 'src/hooks';
import {
  ExchangeCurrencyOption,
  DEFAULT_AMOUNT,
  TradeFormNew,
  AssetAmountNewType,
} from 'src/types';

import {
  ArrowIcon,
  CurrencySelectInputWrapper,
  CustomButton,
  CustomSearch,
  SelectSearchWrapper,
  SwapButton,
  SwapButtonWrapper,
} from './styles';

const DEFAULT_VALUE = '0.00';

type TradePageCalculatorProps = {
  nextStep: () => void;
  defaultFromAssetCode?: string;
  defaultToAssetCode?: string;
  tradeType: TradeType;
  setTradeType: React.Dispatch<React.SetStateAction<TradeType>>;
  fromAsset?: ExchangeCurrencyOption;
  setFromAsset: (val?: ExchangeCurrencyOption) => void;
  toAsset?: ExchangeCurrencyOption;
  setToAsset: (val?: ExchangeCurrencyOption) => void;
  form: TradeFormNew;
  setForm: React.Dispatch<React.SetStateAction<TradeFormNew>>;
  type?: string;
  isSwappable?: boolean;
  changedInputName?: AssetAmountNewType;
  fromPlaceHolder?: string;
  toPlaceHolder?: string;
};

const TradePageCalculator: React.FC<TradePageCalculatorProps> = ({
  nextStep,
  defaultFromAssetCode,
  defaultToAssetCode,
  tradeType,
  setTradeType,
  toAsset,
  setToAsset,
  fromAsset,
  setFromAsset,
  form,
  setForm,
  changedInputName,
  isSwappable,
  type,
  fromPlaceHolder,
  toPlaceHolder,
}) => {
  const { t } = useTranslation();
  const [inputName, setInputName] = useState<AssetAmountNewType>(
    AssetAmountNewType.FROMASSET
  );
  const [assetInputValue, setAssetInputValue] = useState<string>('');
  const [toAssetInputValue, setToAssetInputValue] = useState<string>('');

  const debouncedValue = useDebounce(assetInputValue.toLowerCase(), 600);
  const debouncedToValue = useDebounce(toAssetInputValue.toLowerCase(), 600);

  const [hasSwapped, setHasSwapped] = useState(false);

  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);

  const {
    fromAssets,
    toAssets,
    allAssets,
    setFromAssets,
    setToAssets,
    getPairedAssets,
  } = useExchangeOptionsGe();

  const { balance: fromBalanceInCurrency, loading: fromBalanceIsLoading } =
    useBalanceInSelectedCurrency(fromAsset?.value as CurrencyType, false);

  const { balance: toBalanceInCurrency } = useBalanceInSelectedCurrency(
    toAsset?.value as CurrencyType,
    false
  );

  const {
    response,
    loading: exchangeRateLoading,
    errorMessage,
    clearErrorMessage,
    limitErrorMessage,
    minAmount,
    maxAmount,
    minCost,
    minCostAssetCode,
  } = useExchangeRateGe({
    amount:
      form.type === TransactionAmountType.GROSS
        ? +form.fromAssetAmount
        : +form.toAssetAmount,
    type: form.type,
    fromAssetCode: fromAsset?.value as CurrencyType,
    toAssetCode: toAsset?.value as CurrencyType,
  });

  const limitErrorText = t(limitErrorMessage, {
    from: fromAsset?.value || '',
    to: toAsset?.value || '',
    amount: maxAmount || 0,
    minAmount: minAmount || 0,
    minCost: minCost || 0,
    minCostAssetCode: minCostAssetCode
      ? getAlternativeAssetLabel(minCostAssetCode as CurrencyType)
      : '',
    asset: fromAsset?.value
      ? getAlternativeAssetLabel(fromAsset?.value as CurrencyType)
      : '',
    quoteAsset: toAsset?.value
      ? getAlternativeAssetLabel(toAsset?.value as CurrencyType)
      : '',
  });

  const fromHasBalance = typeof fromBalanceInCurrency === 'number';
  const toHasBalance = typeof toBalanceInCurrency === 'number';

  const fromInputDescription = fromAsset
    ? `${t('common_balance')}: ${getEstimatedAmount(
        fromBalanceInCurrency
      )?.toString()} `
    : '';

  const roundingPrecision =
    inputName === AssetAmountNewType.FROMASSET
      ? fromAsset?.roundingPrecision
      : toAsset?.roundingPrecision;

  const cryptoInputLoading =
    form.type === TransactionAmountType.NET &&
    (+form.toAssetAmount > 0 || +form.fromAssetAmount > 0) &&
    exchangeRateLoading;

  const handleFromAssetSelection = useCallback(
    (value?: string, index?: number, data?: SelectData) => {
      const selectedAsset = data as ExchangeCurrencyOption;

      if (selectedAsset) {
        setFromAsset(selectedAsset);
        setToAssets(getPairedAssets(selectedAsset.value));
      }
    },
    [getPairedAssets, setFromAsset, setToAssets]
  );

  const handleToAssetSelection = useCallback(
    (value?: string, index?: number, data?: SelectData) => {
      const selectedAsset = data as ExchangeCurrencyOption;

      if (selectedAsset) {
        setToAsset(selectedAsset);
      }
      setFromAssets(getPairedAssets(selectedAsset.value));
    },
    [setFromAssets, getPairedAssets, setToAsset]
  );

  const handleFocus = (name?: string) => {
    setInputName(name as AssetAmountNewType);
  };

  const handleOnClose = useCallback(() => {
    setAssetInputValue('');
    setToAssetInputValue('');
  }, []);

  const handleTradeTypeToggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setHasSwapped((prev) => !prev);
      setTradeType((prev: TradeType) =>
        prev === TradeType.BUY ? TradeType.SELL : TradeType.BUY
      );

      if ((fromAsset && fromAssets) || (toAsset && toAssets)) {
        setFromAsset(toAsset);
        setFromAssets(toAssets);
        setToAsset(fromAsset);
        setToAssets(fromAssets);
      }
      const fromAssetAmount = roundDecimal(
        form.fromAssetAmount,
        toAsset?.roundingPrecision
      );
      const toAssetAmount = roundDecimal(
        form.toAssetAmount,
        fromAsset?.roundingPrecision
      );

      setForm((prev) => ({
        ...prev,
        fromAssetAmount: fromAssetAmount.toString(),
        toAssetAmount: toAssetAmount.toString(),
      }));
    },
    [
      form.fromAssetAmount,
      form.toAssetAmount,
      fromAsset,
      fromAssets,
      setForm,
      setFromAsset,
      setFromAssets,
      setToAsset,
      setToAssets,
      setTradeType,
      toAsset,
      toAssets,
    ]
  );

  const handleInputChange = (value: string | number, name: string) => {
    let newValue = value.toString();

    const focusedInputName = name as AssetAmountNewType;

    const startsWithZero = newValue.startsWith('0');
    const isCommaAfterZero = newValue.length > 1 && newValue.startsWith('0.');

    if (errorMessage) {
      clearErrorMessage();
    }

    if (newValue.length === 1 && newValue === '.') {
      newValue = '0.';
    }

    const isNewValueShorter = value.toString().length > newValue.length;
    if (
      (isNewValueShorter && newValue === '0.') ||
      (startsWithZero && !isCommaAfterZero)
    ) {
      newValue = '0';
    }

    if (isNaN(+newValue) || newValue.length > 15) {
      return;
    }

    const currentDecimalsCount = newValue.split('.')[1]?.length;
    const maxDecimals = roundingPrecision || DECIMAL_PRECISION_STABLE_COIN;

    if (currentDecimalsCount > maxDecimals) {
      return;
    }

    const assetType =
      focusedInputName === AssetAmountNewType.FROMASSET
        ? TransactionAmountType.GROSS
        : TransactionAmountType.NET;

    setForm((prev) => {
      if (!+newValue) {
        return {
          fromAssetAmount: DEFAULT_AMOUNT,
          toAssetAmount: DEFAULT_AMOUNT,
          type: assetType,
          [focusedInputName]: newValue,
        };
      }

      return {
        ...prev,
        type: assetType,
        [focusedInputName]: +newValue < 0 ? '' : newValue,
      };
    });
  };

  const renderOptionHeader = useCallback(
    () => (
      <SelectSearchWrapper>
        <CustomSearch
          value={assetInputValue}
          onChangeText={setAssetInputValue}
          placeholder={t('common_search')}
        />
      </SelectSearchWrapper>
    ),
    [assetInputValue, t]
  );

  const toAssetRenderOptionHeader = useCallback(
    () => (
      <SelectSearchWrapper>
        <CustomSearch
          value={toAssetInputValue}
          onChangeText={setToAssetInputValue}
          placeholder={t('common_search')}
        />
      </SelectSearchWrapper>
    ),
    [toAssetInputValue, t]
  );

  const amountErrorMessage = useMemo(() => {
    if (fromBalanceIsLoading) return '';
    if (+debouncedToValue > 0 && +debouncedToValue < 1) {
      return t('validation_amount_min', {
        min: 1,
      });
    }
    if (
      (+form.fromAssetAmount || 0) > fromBalanceInCurrency &&
      fromBalanceInCurrency === 0 &&
      fromAsset?.value
    ) {
      return t('common_empty_balance');
    }
    if (
      (tradeType === TradeType.BUY
        ? +debouncedToValue || 0
        : +debouncedValue || 0) > fromBalanceInCurrency
    ) {
      return t('validation_amount_greater_than');
    }
    return '';
  }, [
    fromBalanceIsLoading,
    debouncedToValue,
    form.fromAssetAmount,
    fromAsset?.value,
    tradeType,
    debouncedValue,
    fromBalanceInCurrency,
    t,
  ]);

  const filteredFromAssets = useMemo(() => {
    return fromAssets?.filter(
      (asset) =>
        asset.value
          .toString()
          .toLowerCase()
          .includes(debouncedValue as string) ||
        asset.name?.toLowerCase().includes(debouncedValue as string)
    );
  }, [fromAssets, debouncedValue]);

  const filteredToAssets = useMemo(() => {
    return toAssets?.filter(
      (asset) =>
        asset.value
          .toString()
          .toLowerCase()
          .includes(debouncedToValue as string) ||
        asset.name?.toLowerCase().includes(debouncedToValue as string)
    );
  }, [toAssets, debouncedToValue]);

  const errorMessages = useMemo(() => {
    if (amountErrorMessage) {
      return amountErrorMessage;
    } else if (errorMessage) {
      return t(errorMessage);
    } else if (limitErrorText) {
      return limitErrorText;
    }
    return '';
  }, [amountErrorMessage, errorMessage, limitErrorText, t]);

  const isFormDisabled =
    !!errorMessage ||
    !!limitErrorMessage ||
    !!amountErrorMessage ||
    !!limitErrorText ||
    exchangeRateLoading ||
    !fromAsset ||
    !form.toAssetAmount ||
    !form.fromAssetAmount ||
    !toAsset;

  useEffect(() => {
    if (
      defaultFromAssetCode &&
      defaultToAssetCode &&
      Object.keys(allAssets).length
    ) {
      const [assetA, assetB] =
        type === TradeType.BUY
          ? [defaultFromAssetCode, defaultToAssetCode]
          : [defaultToAssetCode, defaultFromAssetCode];

      // if (allAssets?.[assetA]?.isStable === allAssets?.[assetB]?.isStable) {
      //   return;
      // }
      setFromAsset(allAssets?.[assetA]);
      setToAsset(allAssets?.[assetB]);
    }

    if (defaultToAssetCode && Object.keys(allAssets).length) {
      handleToAssetSelection(
        undefined,
        undefined,
        allAssets?.[defaultToAssetCode]
      );
    }
    if (defaultFromAssetCode && Object.keys(allAssets).length) {
      handleFromAssetSelection(
        undefined,
        undefined,
        allAssets?.[defaultFromAssetCode]
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAssets, defaultFromAssetCode, defaultToAssetCode, type]);

  useEffect(() => {
    if (!response) return;

    if (!response.calculateExchangeNew) {
      return;
    }

    const toAssetAmount = convertExponentialToDecimal(
      response.calculateExchangeNew.toAssetAmount
    );
    const fromAssetAmount = convertExponentialToDecimal(
      response.calculateExchangeNew.fromAssetAmount
    );

    if (
      +form.toAssetAmount === Number(toAssetAmount) &&
      +form.toAssetAmount !== 0
    ) {
      setForm((prev) => ({
        ...prev,
        fromAssetAmount: fromAssetAmount.toString(),
      }));
    } else if (
      +form.fromAssetAmount === Number(fromAssetAmount) &&
      +form.fromAssetAmount !== 0
    ) {
      setForm((prev) => ({
        ...prev,
        toAssetAmount: toAssetAmount.toString(),
      }));
    } else if (response.calculateExchangeNew.disableReset) {
      setForm((prev) => ({
        ...prev,
        toAssetAmount: toAssetAmount.toString(),
        fromAssetAmount: fromAssetAmount.toString(),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        toAssetAmount: DEFAULT_AMOUNT,
        fromAssetAmount: DEFAULT_AMOUNT,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (changedInputName === AssetAmountNewType.FROMASSET) {
      fromInputRef.current?.focus();
    } else if (changedInputName === AssetAmountNewType.TOASSET) {
      toInputRef.current?.focus();
    }
  }, [changedInputName]);

  const exchangeFeeId =
    response?.calculateExchangeNew?.exchangeFeeId?.toString();

  const hasGelChosen = [fromAsset?.value, toAsset?.value].includes(
    CurrencyType.BGEL
  );
  const feeRangesCurrency = hasGelChosen ? CurrencyType.GEL : CurrencyType.USD;

  return (
    <div>
      <div>
        <>
          <CurrencySelectInputWrapper>
            <CurrencySelectInput
              isUnstable
              placeholder={
                fromPlaceHolder ? fromPlaceHolder : t('common_asset_from')
              }
              name={AssetAmountNewType.FROMASSET}
              selectedValue={fromAsset?.value}
              inputValuePlaceHolder={DEFAULT_VALUE}
              borderBottomFlat
              renderOptionHeader={renderOptionHeader}
              selectPlaceholder={t('common_choose')}
              options={filteredFromAssets}
              inputValue={form.fromAssetAmount}
              inputPlaceHolder={
                fromPlaceHolder ? fromPlaceHolder : t('common_asset_from') + ':'
              }
              emptyStateImage={DesertedIcon}
              emptyStateTitle={t('error_asset_not_found')}
              onChange={handleFromAssetSelection}
              descLoaded={
                tradeType === TradeType.SELL ? fromHasBalance : undefined
              }
              roundingPrecision={fromAsset?.roundingPrecision}
              inputOnChange={handleInputChange}
              description={fromInputDescription}
              isLoading={cryptoInputLoading}
              onFocus={handleFocus}
              ref={fromInputRef}
              onClose={handleOnClose}
              errorMessage={errorMessages}
              balance={fromBalanceInCurrency}
              hideErrorMessage
              forceMenuOpen
              showPercentBar={!!fromAsset}
              modalView
            />
          </CurrencySelectInputWrapper>

          <SwapButtonWrapper>
            <SwapButton
              disabled={!isSwappable}
              onClick={handleTradeTypeToggle}
              isSwapped={hasSwapped}
            >
              {isSwappable ? <SwapIconGe /> : <ArrowIcon />}
            </SwapButton>
          </SwapButtonWrapper>

          <CurrencySelectInput
            borderTopFlat
            placeholder={toPlaceHolder ? toPlaceHolder : t('common_asset_to')}
            selectPlaceholder={t('common_choose')}
            inputValuePlaceHolder={DEFAULT_VALUE}
            name={AssetAmountNewType.TOASSET}
            roundingPrecision={toAsset?.roundingPrecision}
            options={filteredToAssets}
            renderOptionHeader={toAssetRenderOptionHeader}
            selectedValue={toAsset?.value}
            inputValue={form.toAssetAmount}
            emptyStateImage={DesertedIcon}
            emptyStateTitle={t('error_asset_not_found')}
            inputPlaceHolder={
              toPlaceHolder ? toPlaceHolder : t('common_asset_to') + ':'
            }
            onChange={handleToAssetSelection}
            inputOnChange={handleInputChange}
            descLoaded={tradeType === TradeType.BUY ? toHasBalance : undefined}
            onFocus={handleFocus}
            onClose={handleOnClose}
            ref={toInputRef}
            errorMessage={errorMessages}
            forceMenuOpen
            modalView
          />

          <ConvertCommission
            exchangeFeeId={exchangeFeeId}
            selectedAssetCode={feeRangesCurrency}
          />
        </>
      </div>
      <div />

      <CustomButton
        size="lg"
        onClick={() => nextStep()}
        disabled={isFormDisabled}
        isLoading={exchangeRateLoading}
        title={t('trade_confirm_button')}
      />
    </div>
  );
};

export default TradePageCalculator;
